import { useMemo } from 'react';
import { compact } from 'lodash';

import { isSameLanguage } from 'src/data/generation';
import { createStateSelectorHook } from 'src/lib/sweetState';

import { BrandVoicesStore } from './BrandVoicesStore';

const useBrandVoicesState = createStateSelectorHook(BrandVoicesStore, [
  'brandVoicesAssets',
  'loadingBrandVoiceAssets',
]);

export const useBrandVoicesAssets = ({ language } = {}) => {
  const { brandVoicesAssets, loadingBrandVoiceAssets } = useBrandVoicesState();

  return useMemo(() => {
    // Filter brand voices by language if provided
    const filteredBrandVoicesAssets = language
      ? brandVoicesAssets.filter((brandVoice) => isSameLanguage(brandVoice.language, language))
      : brandVoicesAssets;

    const toneOfVoices = filteredBrandVoicesAssets.flatMap((brandVoice) =>
      brandVoice.toneOfVoices?.map((toneOfVoice) => ({
        brandVoiceId: brandVoice.id,
        brandVoiceName: brandVoice.name,
        brandVoiceLanguage: brandVoice.language,
        ...toneOfVoice,
      }))
    );

    const customModels = filteredBrandVoicesAssets.flatMap((brandVoice) =>
      brandVoice.customModels?.map((customModel) => ({
        brandVoiceId: brandVoice.id,
        brandVoiceName: brandVoice.name,
        ...customModel,
      }))
    );

    const customFormulas = filteredBrandVoicesAssets.flatMap((brandVoice) =>
      brandVoice.customFormulas?.map((customFormula) => ({
        brandVoiceId: brandVoice.id,
        brandVoiceName: brandVoice.name,
        ...customFormula,
      }))
    );

    const targetAudiences = filteredBrandVoicesAssets.flatMap((brandVoice) =>
      brandVoice.targetAudiences?.map((targetAudience) => ({
        brandVoiceId: brandVoice.id,
        brandVoiceName: brandVoice.name,
        ...targetAudience,
      }))
    );

    const brandBriefs = filteredBrandVoicesAssets.flatMap((brandVoice) =>
      brandVoice.brandBriefs?.map((brandBrief) => ({
        brandVoiceId: brandVoice.id,
        brandVoiceName: brandVoice.name,
        ...brandBrief,
      }))
    );

    const brandTerms = filteredBrandVoicesAssets.flatMap((brandVoice) =>
      brandVoice.brandTerms?.map((brandTerm) => ({
        brandVoiceId: brandVoice.id,
        brandVoiceName: brandVoice.name,
        brandVoiceLanguage: brandVoice.language,
        ...brandTerm,
      }))
    );

    const customFormats = filteredBrandVoicesAssets.flatMap(
      (brandVoice) => brandVoice.customFormats
    );

    const customFormatGroups = filteredBrandVoicesAssets.flatMap(
      (brandVoice) => brandVoice.customFormatGroups
    );

    return {
      // raw data
      brandVoicesAssets: filteredBrandVoicesAssets,
      loadingBrandVoiceAssets,

      // prepared entities
      customModels: compact(customModels),
      toneOfVoices: compact(toneOfVoices),
      customFormulas: compact(customFormulas),
      targetAudiences: compact(targetAudiences),
      brandBriefs: compact(brandBriefs),
      brandTerms: compact(brandTerms),
      customFormats: compact(customFormats),
      customFormatGroups: compact(customFormatGroups),
    };
  }, [brandVoicesAssets, loadingBrandVoiceAssets, language]);
};
